import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@a7y/tailwind-catalyst";
import { useQuery } from "@tanstack/react-query";
import { Scale } from "lucide-react";
import { formatCents, LoadingPanel } from "./shared";
import type { ReconciliationRow } from "./types";

export function ReconciliationPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["affiliate-reconciliation"],
    queryFn: () => api.affiliates.getReconciliation(),
  });

  const rows: ReconciliationRow[] = data?.rows ?? [];
  // A7Y-1900 supplies the Rewardful/shadow side; until then we degrade cleanly.
  const rewardfulAvailable = data?.rewardfulAvailable ?? false;

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="Reconciliation"
        subtitle="Native first-party vs. legacy Rewardful attribution, by affiliate"
      />

      {!rewardfulAvailable && (
        <div className="mb-4 rounded-lg bg-amber-50 px-4 py-2.5 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
          Rewardful / shadow data is not yet available (arrives with A7Y-1900). Only the native
          side is shown below.
        </div>
      )}

      {isLoading ? (
        <LoadingPanel />
      ) : rows.length === 0 ? (
        <div className="py-16 text-center">
          <Scale className="mx-auto h-12 w-12 text-zinc-400" />
          <Text className="mt-2 text-zinc-500">Nothing to reconcile yet</Text>
        </div>
      ) : (
        <Table dense striped>
          <TableHead>
            <TableRow>
              <TableHeader>Affiliate</TableHeader>
              <TableHeader>Native visitors</TableHeader>
              <TableHeader>Native leads</TableHeader>
              <TableHeader>Native conv.</TableHeader>
              <TableHeader>Native commission</TableHeader>
              <TableHeader>Rewardful conv.</TableHeader>
              <TableHeader>Rewardful commission</TableHeader>
              <TableHeader>Delta</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const deltaCents = r.rewardful
                ? r.native.commissionCents - r.rewardful.commissionCents
                : null;
              return (
                <TableRow key={r.affiliateId}>
                  <TableCell className="text-sm">
                    {r.email || <span className="font-mono text-xs">{r.affiliateId}</span>}
                  </TableCell>
                  <TableCell>{r.native.visitors}</TableCell>
                  <TableCell>{r.native.leads}</TableCell>
                  <TableCell>{r.native.conversions}</TableCell>
                  <TableCell className="font-medium">
                    {formatCents(r.native.commissionCents)}
                  </TableCell>
                  <TableCell>{r.rewardful?.conversions ?? "—"}</TableCell>
                  <TableCell>
                    {r.rewardful ? formatCents(r.rewardful.commissionCents) : "—"}
                  </TableCell>
                  <TableCell>
                    {deltaCents == null ? (
                      "—"
                    ) : deltaCents === 0 ? (
                      <Badge color="green">MATCH</Badge>
                    ) : (
                      <Badge color="amber">{formatCents(deltaCents)}</Badge>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
